// MaturaPowerTimesRootClosedTask.js
// Zadanie maturalne zamknięte: iloczyn potęgi i pierwiastka z potęgi
// typu 256 · ∛(8^2), zawsze z wynikiem w postaci a^k (a ∈ {2,3,5}).

import React from "react";
import { InlineMath, BlockMath } from "react-katex";
import ABCDInput from "../../../../components/inputs/ABCDInput";

const LETTERS = ["A", "B", "C", "D"];
const pick = (arr) => arr[Math.floor(Math.random() * arr.length)];

const greatestCommonDivisor = (first, second) => {
  let a = Math.abs(first);
  let b = Math.abs(second);

  while (b !== 0) {
    [a, b] = [b, a % b];
  }

  return a;
};

export const hasObviousRootExponentCancellation = (rootDegree, exponent) =>
  greatestCommonDivisor(rootDegree, exponent) > 1;

// konfiguracja rodzin 2,3,5 – jakie wykładniki w treści
const FAMILY_CONFIG = {
  2: {
    intExps: [4, 5, 6, 7, 8],   // 2^4=16 ... 2^8=256
    radBaseExps: [2, 3, 4],     // 2^2=4, 2^3=8, 2^4=16
    maxResultExp: 16,
  },
  3: {
    intExps: [2, 3, 4],         // 3^2=9, 3^3=27, 3^4=81
    radBaseExps: [2, 3, 4],     // 3^2=9, 3^3=27, 3^4=81
    maxResultExp: 14,
  },
  5: {
    intExps: [2, 3],            // 5^2=25, 5^3=125
    radBaseExps: [2, 3],        // 5^2=25, 5^3=125
    maxResultExp: 12,
  },
};

function rootWithPowerLatex(q, baseNum, p) {
  const inside = `${baseNum}^{${p}}`;
  if (q === 2) return `\\sqrt{${inside}}`;
  return `\\sqrt[${q}]{${inside}}`;
}

const expToLatex = (e) => (e < 0 ? `-${Math.abs(e)}` : `${e}`);

// ============================================================================
// GENERATOR
// ============================================================================

function createJune2025OriginalTask() {
  const options = [8, 10, 16, 36].map((exp, index) => ({
    label: LETTERS[index],
    latex: `2^{${exp}}`,
    exp,
    isCorrect: exp === 10,
  }));

  return {
    type: "MaturaPowerTimesRootClosedTask",
    data: {
      base: 2,
      a: 8,
      r: 3,
      q: 3,
      p: 2,
      Bint: 2,
      E: 10,
      intFactor: 256,
      radBaseNum: 8,
      leftLatex: "256\\cdot\\sqrt[3]{8^{2}}",
      options,
      correctIndex: 1,
    },
    source: {
      variant: "original",
      exam: "MATURA",
      session: "2025_CZE",
      taskNumber: 2,
    },
  };
}

export function generateTask(generationOptions = {}) {
  if (
    generationOptions.variant === "original" &&
    generationOptions.session === "2025_CZE"
  ) {
    return createJune2025OriginalTask();
  }

  let base, cfg, a, r, q, p, Bint, E;
  let intFactor, radBaseNum, leftLatex;

  let tries = 0;
  while (tries < 200) {
    tries += 1;

    base = pick([2, 3, 5]);
    cfg = FAMILY_CONFIG[base];

    a = pick(cfg.intExps);        // wykładnik dla liczby z przodu
    r = pick(cfg.radBaseExps);    // wykładnik dla liczby pod pierwiastkiem
    q = pick([2, 3, 4, 5]);       // stopień pierwiastka
    // ⬇⬇ NIE losujemy p = 1, żeby nie było 4^1 itd.
    p = pick([2, 3]);             // wykładnik wewnątrz pierwiastka

    // W zapisie zadania nie skracamy od razu wykładnika ze stopniem
    // pierwiastka, np. √(8²), ∛(25³) ani ⁴√(4²).
    if (hasObviousRootExponentCancellation(q, p)) continue;

    const m = r * p;              // (base^r)^p = base^{m}
    if (m % q !== 0) continue;    // musi się dzielić, żeby wynik był całkowity

    Bint = m / q;                 // wkład exponentu z pierwiastka
    E = a + Bint;                 // wynikowy wykładnik

    // wynik nie może być za mały ani za duży, i nie chcemy np. a^1
    if (E <= 2 || E > cfg.maxResultExp) continue;

    intFactor = Math.pow(base, a);   // np. 2^8 = 256
    radBaseNum = Math.pow(base, r);  // np. 2^3 = 8

    // ⬇⬇ Bezpiecznik: liczba z przodu i liczba pod pierwiastkiem
    // NIE mogą być takie same (np. 125 i 125).
    if (intFactor === radBaseNum) continue;

    const rootPart = rootWithPowerLatex(q, radBaseNum, p);
    leftLatex = `${intFactor}\\cdot ${rootPart}`;

    break;
  }

  // poprawna odpowiedź: base^E
  const correctExp = E;

  // kandydaci na błędne wykładniki
  const candidates = [
    a + r * p,       // zignorowanie pierwiastka: a + m
    a + Bint * 2,    // podwojenie wkładu z pierwiastka
    Bint,            // wzięcie samego pierwiastka
    correctExp + 1,
    correctExp - 1,
  ];

  const distractorExps = new Set();
  for (const c of candidates) {
    if (
      distractorExps.size >= 3 ||
      c === correctExp ||
      c <= 0 ||
      c === 1 ||                 // ⬅ unikamy wykładnika 1
      c > cfg.maxResultExp + 6
    ) {
      continue;
    }
    distractorExps.add(c);
  }

  while (distractorExps.size < 3) {
    const rExp = pick([
      correctExp + 2,
      correctExp - 2,
      correctExp + 3,
      correctExp - 3,
    ]);
    if (rExp > 0 && rExp !== 1 && rExp !== correctExp) {
      distractorExps.add(rExp);
    }
  }

  const optionsRaw = [
    { exp: correctExp, isCorrect: true },
    ...Array.from(distractorExps).map((e) => ({ exp: e, isCorrect: false })),
  ];

  const shuffled = optionsRaw
    .map((o) => ({ ...o, sortKey: Math.random() }))
    .sort((a, b) => a.sortKey - b.sortKey)
    .map((o, idx) => ({
      label: LETTERS[idx],
      latex: `${base}^{${expToLatex(o.exp)}}`,
      isCorrect: o.isCorrect,
      exp: o.exp,
    }));

  const correctIndex = shuffled.findIndex((o) => o.isCorrect);

  return {
    type: "MaturaPowerTimesRootClosedTask",
    data: {
      base,
      a,         // wykładnik liczby przed pierwiastkiem
      r,         // wykładnik liczby pod pierwiastkiem (radBaseNum = base^r)
      q,         // stopień pierwiastka
      p,         // wykładnik pod pierwiastkiem (≥ 2)
      Bint,      // m/q
      E: correctIndex >= 0 ? correctExp : correctExp, // safety
      intFactor,
      radBaseNum,
      leftLatex,
      options: shuffled,
      correctIndex,
    },
  };
}

// ============================================================================
// RENDER TASK
// ============================================================================

export function renderTask({ data }) {
  return (
    <div className="space-y-3" style={{ textAlign: "left" }}>
      <p>
        <strong>Zadanie maturalne – zamknięte.</strong>
      </p>
      <p>Dokończ zdanie. Wybierz właściwą odpowiedź spośród podanych.</p>
      <p>
        Liczba <InlineMath math={data.leftLatex} /> jest równa:
      </p>

      <div style={{ marginTop: 8 }}>
        {data.options.map((opt) => (
          <div key={opt.label} style={{ marginBottom: 4 }}>
            <InlineMath math={`${opt.label}.\\; ${opt.latex}`} />
          </div>
        ))}
      </div>
    </div>
  );
}

// ============================================================================
// INPUT
// ============================================================================

export function renderInput({ data }, userAnswer, onChange, disabled) {
  const { options } = data;
  const choiceIndex =
    userAnswer && typeof userAnswer.choiceIndex === "number"
      ? userAnswer.choiceIndex
      : null;

  const letterOptions = options.map((opt, idx) => ({
    value: idx,
    label: opt.label,
    textNode: opt.label,
  }));

  const handleChange = (idx) => {
    const opt = options[idx];
    onChange?.({
      choiceIndex: idx,
      label: opt.label,
      latex: opt.latex,
    });
  };

  return (
    <ABCDInput
      options={letterOptions}
      value={choiceIndex}
      onChange={handleChange}
      disabled={disabled}
    />
  );
}

// ============================================================================
// CHECK ANSWER
// ============================================================================

export function checkAnswer({ data }, userAnswer) {
  if (!userAnswer || typeof userAnswer.choiceIndex !== "number") return false;
  return userAnswer.choiceIndex === data.correctIndex;
}

// ============================================================================
// RENDER ANSWER & USER ANSWER
// ============================================================================

export function renderAnswer({ data }) {
  const correct = data.options[data.correctIndex];
  return <InlineMath math={`${correct.label}.\\; ${correct.latex}`} />;
}

export function renderUserAnswer(userAnswer) {
  if (
    !userAnswer ||
    typeof userAnswer.choiceIndex !== "number" ||
    !userAnswer.label ||
    !userAnswer.latex
  ) {
    return <i>(brak odpowiedzi)</i>;
  }

  const { label, latex } = userAnswer;
  return (
    <div style={{ marginTop: "1rem" }}>
      <span>Twoja odpowiedź: </span>
      <InlineMath math={`${label}.\\; ${latex}`} />
    </div>
  );
}

// ============================================================================
// WYJAŚNIENIE
// ============================================================================

export function renderExplanation({ data }) {
  const { base, a, r, q, p, Bint, E, intFactor, radBaseNum, leftLatex } = data;

  const step1Top = `${intFactor}= ${base}^{${a}}`;
  const step1RootBase = `${radBaseNum}= ${base}^{${r}}`;

  const m = r * p;

  const rootWithNum = rootWithPowerLatex(q, radBaseNum, p);
  const rootAsBasePow =
    q === 2
      ? `\\sqrt{${base}^{${m}}}`
      : `\\sqrt[${q}]{${base}^{${m}}}`;

  return (
    <div className="space-y-3" style={{ textAlign: "left" }}>
      <div>
        <strong>Krok 1.</strong> Zapisujemy liczby jako potęgi tej samej
        podstawy <InlineMath math={`${base}`} />:
      </div>
      <BlockMath math={step1Top} />
      <BlockMath math={step1RootBase} />

      <div>
        <strong>Krok 2.</strong> Podstawiamy te równości do wyrażenia.
      </div>
      <BlockMath
        math={`${leftLatex}= ${base}^{${a}}\\cdot ${rootWithNum}`}
      />
      <BlockMath
        math={`= ${base}^{${a}}\\cdot ${rootAsBasePow}`}
      />

      <div>
        <strong>Krok 3.</strong> Korzystamy z definicji pierwiastka{" "}
        <InlineMath math={"\\sqrt[q]{a^{k}} = a^{\\tfrac{k}{q}}"} />.
      </div>
      <BlockMath
        math={`${rootAsBasePow}= ${base}^{\\tfrac{${m}}{${q}}} = ${base}^{${Bint}}`}
      />

      <div>
        <strong>Krok 4.</strong> Mnożymy potęgi o tej samej podstawie.
      </div>
      <BlockMath
        math={`${base}^{${a}}\\cdot ${base}^{${Bint}} = ${base}^{${a}+${Bint}} = ${base}^{${E}}`}
      />

      <div>
        <strong>Wynik:</strong>{" "}
        <InlineMath math={`${base}^{${E}}`} />.
      </div>
    </div>
  );
}
